import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { fetchOrder } from 'entities/Order';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';

import { buildSellersDataBySeller } from '../lib/buildSellersDataBySeller';
import { OrderProposalFilterSchema } from '../types/OrderProposalFilterSchema';

const initialState: OrderProposalFilterSchema = {
    orderItems: [],
    filter: 'All',
    filteredItems: [],
    filterSellersDataBySeller: {},
    poEditState: undefined,
};

/**
 * Updates sellers data and preserves/exits PO edit state based on supplier existence
 */
const updateSellersDataAndPreservePoEditState = (
    state: OrderProposalFilterSchema,
    newOrderItems: OrderProposalFilterSchema['orderItems'],
) => {
    const newSellersDataBySeller = buildSellersDataBySeller(newOrderItems);
    state.filterSellersDataBySeller = newSellersDataBySeller;

    // If PO is being edited, check if the supplier still has items
    // If supplier is entirely removed, exit PO edit mode
    if (state.poEditState) {
        const editingSellerId = state.poEditState.editingSellerId;
        const supplierStillExists = editingSellerId in newSellersDataBySeller;

        if (!supplierStillExists) {
            state.poEditState = undefined;
        }
    }
};

export const OrderProposalFilterSlice = createSlice({
    name: 'OrderProposalFilter',
    initialState,
    reducers: {
        setFilter: (state, action: PayloadAction<string>) => {
            state.filter = action.payload;
        },
        setFilteredItems: (state, action) => {
            state.filteredItems = action.payload;
        },
        setPoEditState: (
            state,
            action: PayloadAction<
                | {
                      editingSellerId: string;
                      tempPoValue: string;
                      originalPoValue: string;
                      hasError?: boolean;
                  }
                | undefined
            >,
        ) => {
            state.poEditState = action.payload;
        },
        setPoFieldError: (state, action: PayloadAction<boolean>) => {
            if (state.poEditState) {
                state.poEditState.hasError = action.payload;
            }
        },
        setPoTempValue: (
            state,
            action: PayloadAction<{ sellerId: string; tempValue: string }>,
        ) => {
            if (
                state.poEditState &&
                state.poEditState.editingSellerId === action.payload.sellerId
            ) {
                state.poEditState.tempPoValue = action.payload.tempValue;
                state.poEditState.hasError = false;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrder.fulfilled, (state, action) => {
                const { data } = action.payload;
                state.orderItems = data.items;
                updateSellersDataAndPreservePoEditState(state, data.items);
            })
            .addCase(deleteOrderItem.fulfilled, (state, action) => {
                const deletedItem = action.payload;
                const newOrderItems = [...state.orderItems];
                const foundIndex = newOrderItems.findIndex(
                    (item) => item.id === deletedItem.id,
                );

                if (foundIndex !== -1) {
                    newOrderItems.splice(foundIndex, 1);
                }

                state.orderItems = newOrderItems;
                updateSellersDataAndPreservePoEditState(state, newOrderItems);
            })
            .addCase(editOrderItem.fulfilled, (state, action) => {
                const editedItem = action.payload;
                const newOrderItems = [...state.orderItems];
                const foundIndex = newOrderItems.findIndex(
                    (item) => item.id === editedItem.id,
                );

                if (foundIndex !== -1) {
                    newOrderItems[foundIndex] = editedItem;
                }

                state.orderItems = newOrderItems;
                updateSellersDataAndPreservePoEditState(state, newOrderItems);
            });
    },
});

export const { actions: orderProposalFilterActions } = OrderProposalFilterSlice;
export const { reducer: orderProposalFilterReducer } = OrderProposalFilterSlice;
